import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, RefreshCw, Search, ShieldAlert, Star, Database, AlertTriangle } from 'lucide-react';
import { apiFetch } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';

export interface TopVulnerabilityItem {
  cveId: string;
  title: string;
  summary: string;
  vendorProject?: string;
  product?: string;
  cvssScore?: number | null;
  cvssSeverity?: string | null;
  cvssVector?: string | null;
  epssScore?: number | null;
  epssPercentile?: number | null;
  cisaKev: boolean;
  knownExploited?: boolean;
  knownRansomwareCampaignUse?: string | null;
  dateAdded?: string | null;
  dueDate?: string | null;
  publishedDate?: string | null;
  lastModifiedDate?: string | null;
  patchAvailable?: boolean;
  officialCvssUrl: string;
  advisoryUrl?: string | null;
  blogUrl?: string | null;
  priorityScore: number;
  sourceTags: string[];
  requiredAction?: string | null;
}

interface TopVulnerabilitiesResponse {
  data: TopVulnerabilityItem[];
  cached?: boolean;
  fetchedAt?: string | null;
  count?: number;
  error?: string;
}

interface TopVulnerabilitiesPanelProps {
  customerId?: string;
}

type SeverityFilter = 'all' | 'critical' | 'high' | 'medium' | 'kev';

function fmtDate(value?: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
}

function fmtPercent(value?: number | null): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  return `${Math.round(value * 1000) / 10}%`;
}

function scoreTone(score?: number | null): string {
  if (typeof score !== 'number') return 'bg-muted text-muted-foreground';
  if (score >= 9) return 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-200';
  if (score >= 7) return 'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-200';
  if (score >= 4) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-200';
  return 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-200';
}

function priorityTone(score: number): string {
  if (score >= 80) return 'bg-red-600 text-white';
  if (score >= 60) return 'bg-orange-500 text-white';
  if (score >= 40) return 'bg-yellow-500 text-white';
  return 'bg-slate-500 text-white';
}

export function TopVulnerabilitiesPanel({ customerId }: TopVulnerabilitiesPanelProps) {
  const { canCapability } = useAuth();
  const canRefresh = canCapability('threat_intel', 'refresh_top_vulnerabilities');
  const [items, setItems] = useState<TopVulnerabilityItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [severity, setSeverity] = useState<SeverityFilter>('all');
  const [kevOnly, setKevOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('limit', '50');
      if (customerId) qs.set('customerId', customerId);
      const data = await apiFetch<TopVulnerabilitiesResponse>(`/threat-intel/top-vulnerabilities?${qs.toString()}`);
      setItems(data.data ?? []);
      setFetchedAt(data.fetchedAt ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load top vulnerabilities.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { void load(); }, [load]);

  async function refresh() {
    if (!canRefresh) return;
    setRefreshing(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('limit', '50');
      if (customerId) qs.set('customerId', customerId);
      const data = await apiFetch<TopVulnerabilitiesResponse>(`/threat-intel/top-vulnerabilities/refresh?${qs.toString()}`, { method: 'POST' });
      setItems(data.data ?? []);
      setFetchedAt(data.fetchedAt ?? new Date().toISOString());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not refresh vulnerability intelligence.');
    } finally {
      setRefreshing(false);
    }
  }

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter((item) => {
      if (kevOnly && !item.cisaKev) return false;
      if (severity === 'critical' && !(Number(item.cvssScore) >= 9)) return false;
      if (severity === 'high' && !(Number(item.cvssScore) >= 7 && Number(item.cvssScore) < 9)) return false;
      if (severity === 'medium' && !(Number(item.cvssScore) >= 4 && Number(item.cvssScore) < 7)) return false;
      if (severity === 'kev' && !item.cisaKev) return false;
      if (!needle) return true;
      return [item.cveId, item.title, item.summary, item.vendorProject, item.product, ...(item.sourceTags ?? [])]
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }, [items, kevOnly, query, severity]);

  const statCritical = items.filter((i) => Number(i.cvssScore) >= 9).length;
  const statKev = items.filter((i) => i.cisaKev).length;
  const statRansomware = items.filter((i) => String(i.knownRansomwareCampaignUse ?? '').toLowerCase() === 'known').length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-border bg-card p-4 shadow">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Tracked CVEs</div>
          <div className="mt-2 text-3xl font-bold text-foreground">{items.length}</div>
          <div className="mt-1 text-xs text-muted-foreground">Cached ranked vulnerability board</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 shadow">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Critical CVSS</div>
          <div className="mt-2 text-3xl font-bold text-red-600">{statCritical}</div>
          <div className="mt-1 text-xs text-muted-foreground">CVSS 9.0 and above</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 shadow">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">CISA KEV</div>
          <div className="mt-2 text-3xl font-bold text-orange-600">{statKev}</div>
          <div className="mt-1 text-xs text-muted-foreground">Known exploited vulnerabilities</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 shadow">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Ransomware Use</div>
          <div className="mt-2 text-3xl font-bold text-purple-600">{statRansomware}</div>
          <div className="mt-1 text-xs text-muted-foreground">Marked known in CISA KEV</div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card shadow overflow-hidden">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 border-b border-border bg-muted/30 p-4">
          <div>
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <ShieldAlert size={18} className="text-primary" /> Top Vulnerabilities
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Ranked from CISA KEV, NVD CVSS metadata and EPSS where available. {fetchedAt ? `Last refreshed ${fmtDate(fetchedAt)}.` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-2.5 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search CVE, vendor, product…"
                className="w-64 rounded-lg border border-border bg-background pl-8 pr-3 py-2 text-xs text-foreground focus:outline-none focus:border-primary"
              />
            </div>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as SeverityFilter)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground focus:outline-none focus:border-primary"
            >
              <option value="all">All severity</option>
              <option value="critical">Critical CVSS</option>
              <option value="high">High CVSS</option>
              <option value="medium">Medium CVSS</option>
              <option value="kev">CISA KEV only</option>
            </select>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground select-none">
              <input type="checkbox" checked={kevOnly} onChange={(e) => setKevOnly(e.target.checked)} className="accent-primary" /> KEV only
            </label>
            <button
              type="button"
              onClick={() => void refresh()}
              disabled={!canRefresh || refreshing}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
              title={canRefresh ? 'Refresh vulnerability feed cache' : 'Missing refresh_top_vulnerabilities permission'}
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
            {error}
          </div>
        )}

        {loading && <div className="p-5 text-sm text-muted-foreground">Loading vulnerability intelligence…</div>}

        {!loading && filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Database size={28} className="mx-auto mb-2 opacity-60" />
            No vulnerabilities matched the current filters. Use Refresh to populate the cache if this is the first run.
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/70">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Priority</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">CVE</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Vendor / Product</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Summary</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">CVSS</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">EPSS</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Exploit</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Links</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.cveId} className="border-t border-border align-top hover:bg-muted/30">
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`rounded-full px-2 py-1 text-[11px] font-bold ${priorityTone(item.priorityScore)}`}>{item.priorityScore}</span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="font-mono font-semibold text-primary">{item.cveId}</div>
                      <div className="text-[10px] text-muted-foreground">Modified {fmtDate(item.lastModifiedDate)}</div>
                    </td>
                    <td className="px-3 py-3 min-w-[180px]">
                      <div className="font-semibold text-foreground">{item.vendorProject || 'Unknown vendor'}</div>
                      <div className="text-muted-foreground">{item.product || 'Unknown product'}</div>
                    </td>
                    <td className="px-3 py-3 min-w-[320px] max-w-[560px]">
                      <div className="font-semibold text-foreground line-clamp-2">{item.title}</div>
                      <div className="mt-1 text-muted-foreground line-clamp-3">{item.summary}</div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {item.sourceTags.map((tag) => (
                          <span key={tag} className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">{tag}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`rounded-full px-2 py-1 font-bold ${scoreTone(item.cvssScore)}`}>{typeof item.cvssScore === 'number' ? item.cvssScore.toFixed(1) : 'N/A'}</span>
                      <div className="mt-1 text-[10px] text-muted-foreground">{item.cvssSeverity || 'Unknown'}</div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="font-semibold text-foreground">{fmtPercent(item.epssScore)}</div>
                      <div className="text-[10px] text-muted-foreground">Pct {fmtPercent(item.epssPercentile)}</div>
                    </td>
                    <td className="px-3 py-3 min-w-[160px]">
                      {item.cisaKev ? (
                        <div className="space-y-1">
                          <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-700 dark:bg-orange-950 dark:text-orange-200"><Star size={10} /> CISA KEV</span>
                          <div className="text-[10px] text-muted-foreground">Due {fmtDate(item.dueDate)}</div>
                        </div>
                      ) : <span className="text-muted-foreground">-</span>}
                      {String(item.knownRansomwareCampaignUse ?? '').toLowerCase() === 'known' && (
                        <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-semibold text-purple-700 dark:bg-purple-950 dark:text-purple-200"><AlertTriangle size={10} /> ransomware</div>
                      )}
                    </td>
                    <td className="px-3 py-3 min-w-[150px]">
                      <div className="flex flex-col gap-1">
                        <a href={item.officialCvssUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                          NVD / CVSS <ExternalLink size={11} />
                        </a>
                        {item.advisoryUrl ? (
                          <a href={item.advisoryUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                            Advisory / blog <ExternalLink size={11} />
                          </a>
                        ) : <span className="text-muted-foreground">No advisory link</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
